import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Search, Users, Archive, Radio } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { cn } from '@/lib/utils';
import { aurora, auroraPalette } from '@/components/aurora/tokens';
import { EmptyState } from '@/components/aurora/EmptyState';
import { MobileTopBar } from '@/components/mobile/messenger';
import { messengerApi } from '@/pages/messenger/lib/messengerApi';
import CreateWorkgroupModal from '@/pages/messenger/workgroup-hub/CreateWorkgroupModal';
import {
  CATEGORY_LABELS,
  type Workgroup,
  type WorkgroupCategory,
} from '@/pages/messenger/workgroup-hub/types';

const THEME_DOT: Record<string, string> = {
  blue: 'bg-blue-500', emerald: 'bg-emerald-500', purple: 'bg-purple-500',
  orange: 'bg-orange-500', pink: 'bg-pink-500', cyan: 'bg-cyan-500',
  amber: 'bg-amber-500', indigo: 'bg-indigo-500', red: 'bg-red-500', teal: 'bg-teal-500',
};
const THEME_BORDER: Record<string, string> = {
  blue: 'border-l-blue-500', emerald: 'border-l-emerald-500', purple: 'border-l-purple-500',
  orange: 'border-l-orange-500', pink: 'border-l-pink-500', cyan: 'border-l-cyan-500',
  amber: 'border-l-amber-500', indigo: 'border-l-indigo-500', red: 'border-l-red-500', teal: 'border-l-teal-500',
};
function accentDot(theme?: string | null): string { return (theme && THEME_DOT[theme]) || 'bg-indigo-500'; }
function accentBorder(theme?: string | null): string { return (theme && THEME_BORDER[theme]) || 'border-l-indigo-500'; }

type ActiveHuddleMap = Record<string, { recording: boolean }>;

interface ActiveHuddleProbe {
  id: string;
  recording_requested?: boolean | null;
}

export default function MobileWorkgroups() {
  const navigate = useNavigate();
  const [workgroups, setWorkgroups] = useState<Workgroup[]>([]);
  const [archived, setArchived] = useState<Workgroup[]>([]);
  const [activeHuddles, setActiveHuddles] = useState<ActiveHuddleMap>({});
  const [unreadByConv, setUnreadByConv] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<WorkgroupCategory | ''>('');
  const [showArchived, setShowArchived] = useState(false);

  const tenantSlug = useMemo(() => window.location.pathname.split('/')[1] || '', []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<Workgroup[]>('/messenger/workgroups');
      setWorkgroups(res.data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadArchived = useCallback(async () => {
    try {
      const res = await apiClient.get<Workgroup[]>('/messenger/workgroups/archived');
      setArchived(res.data ?? []);
    } catch {
      setArchived([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showArchived) loadArchived(); }, [showArchived, loadArchived]);

  // Mappa unread_count per conversation_id usando l'elenco conversazioni
  // messenger v2. Refresh ogni 30s, allineato al polling di MobileChat.
  useEffect(() => {
    if (showArchived) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await messengerApi.listConversations({});
        if (cancelled) return;
        const map: Record<string, number> = {};
        for (const c of res.data ?? []) {
          if (c.unread_count > 0) map[c.id] = c.unread_count;
        }
        setUnreadByConv(map);
      } catch {
        /* noop */
      }
    };
    fetchUnread();
    const t = window.setInterval(fetchUnread, 30000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, [showArchived]);

  // Probe huddle attive solo per i gruppi correnti (non archiviati).
  useEffect(() => {
    if (showArchived || workgroups.length === 0) return;
    let cancelled = false;
    const probe = async () => {
      const entries = await Promise.all(workgroups.map(async (w) => {
        try {
          const res = await apiClient.get<ActiveHuddleProbe | null>(
            `/messenger/workgroups/${w.id}/huddle/active`,
          );
          if (res.data) return [w.id, { recording: !!res.data.recording_requested }] as const;
          return null;
        } catch {
          return null;
        }
      }));
      if (cancelled) return;
      const map: ActiveHuddleMap = {};
      for (const e of entries) if (e) map[e[0]] = e[1];
      setActiveHuddles(map);
    };
    probe();
    const id = window.setInterval(probe, 20000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [workgroups, showArchived]);

  const list = showArchived ? archived : workgroups;
  const filtered = list.filter(w => {
    if (categoryFilter && w.category !== categoryFilter) return false;
    if (query && !w.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  async function restoreWg(id: string) {
    await apiClient.patch(`/messenger/workgroups/${id}/restore`, {});
    loadArchived();
    load();
  }

  return (
    <div className="flex flex-col h-[100dvh] bg-background">
      <MobileTopBar
        title="Gruppi"
        subtitle={showArchived ? 'Archivio' : undefined}
        actions={[{
          id: 'new-workgroup',
          label: 'Nuovo gruppo',
          icon: <Plus className="w-5 h-5" />,
          onClick: () => setShowCreate(true),
        }]}
      />

      <div
        className="px-3 py-2 border-b border-border bg-background/95 backdrop-blur-md sticky z-10"
        style={{ top: 'calc(env(safe-area-inset-top) + 56px)' }}
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca gruppo..."
            aria-label="Cerca gruppo"
            className="w-full h-10 bg-muted border border-border rounded-full pl-9 pr-3 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            style={{ outlineColor: auroraPalette.indigo }}
          />
        </div>
        <div className="mt-2 flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
          <FilterPill
            active={!categoryFilter && !showArchived}
            onClick={() => { setCategoryFilter(''); setShowArchived(false); }}
            label="Tutti"
          />
          {(Object.keys(CATEGORY_LABELS) as WorkgroupCategory[]).map(c => (
            <FilterPill
              key={c}
              active={categoryFilter === c && !showArchived}
              onClick={() => { setCategoryFilter(c); setShowArchived(false); }}
              label={CATEGORY_LABELS[c]}
            />
          ))}
          <FilterPill
            active={showArchived}
            onClick={() => setShowArchived(v => !v)}
            label="Archiviati"
            icon={<Archive className="w-3 h-3" />}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            <Loader2 className="animate-spin inline" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            illustration="no-workgroups"
            title={showArchived ? 'Nessun workgroup archiviato' : 'Nessun gruppo'}
            subtitle={showArchived
              ? 'I gruppi archiviati appariranno qui. Tap per ripristinare.'
              : 'Crea il tuo primo gruppo per coordinare chat, task, eventi e file.'}
            cta={!showArchived && (
              <button
                onClick={() => setShowCreate(true)}
                className="px-4 py-2 rounded-full text-white text-sm inline-flex items-center gap-2"
                style={{ background: auroraPalette.indigo }}
              >
                <Plus className="w-4 h-4" /> Crea primo gruppo
              </button>
            )}
          />
        ) : (
          <ul className="space-y-2" role="list">
            {filtered.map(wg => {
              const dot = accentDot(wg.theme_color);
              const border = accentBorder(wg.theme_color);
              const huddle = activeHuddles[wg.id];
              const unread = wg.conversation_id ? (unreadByConv[wg.conversation_id] ?? 0) : 0;
              return (
                <li key={wg.id}>
                  <button
                    onClick={() => showArchived
                      ? restoreWg(wg.id)
                      : navigate(`/${tenantSlug}/messenger/workgroups/${wg.id}`)}
                    className={cn(
                      'w-full text-left bg-card border border-border border-l-4 rounded-2xl p-3 flex items-center gap-3 active:scale-[0.99] transition',
                      border,
                    )}
                    data-testid={`mobile-workgroup-card-${wg.id}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-muted border border-border flex items-center justify-center text-foreground text-sm font-semibold shrink-0 overflow-hidden">
                      {wg.avatar_url ? (
                        <img src={wg.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span aria-hidden>{wg.name.substring(0, 2).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={cn(aurora.type.bodyStrong, 'truncate text-foreground')}>
                          {wg.name}
                        </span>
                        {huddle && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-[10px] uppercase tracking-wide text-emerald-300 font-semibold shrink-0"
                            aria-label="Video Call Live attiva"
                          >
                            <Radio className={cn('w-2.5 h-2.5', huddle.recording && 'animate-pulse text-red-300')} />
                            {huddle.recording ? 'REC' : 'LIVE'}
                          </span>
                        )}
                        {unread > 0 && (
                          <span
                            className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-[11px] font-semibold text-white shrink-0"
                            style={{ background: auroraPalette.indigo }}
                            aria-label={`${unread} messaggi non letti`}
                            data-testid={`mobile-workgroup-unread-${wg.id}`}
                          >
                            {unread > 99 ? '99+' : unread}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {wg.category && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-border text-muted-foreground shrink-0">
                            <span className={cn('w-1.5 h-1.5 rounded-full', dot)} aria-hidden />
                            {CATEGORY_LABELS[wg.category]}
                          </span>
                        )}
                        {wg.description && (
                          <p className="text-xs text-muted-foreground truncate">
                            {wg.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground/80 mt-1">
                        <span className="inline-flex items-center gap-1">
                          <Users className="w-3 h-3" /> {wg.member_count ?? 0}
                        </span>
                        {showArchived && (
                          <span className="text-amber-400">tap per ripristinare</span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showCreate && (
        <CreateWorkgroupModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            navigate(`/${tenantSlug}/messenger/workgroups/${id}`);
          }}
        />
      )}
    </div>
  );
}

interface FilterPillProps {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: ReactNode;
}

function FilterPill({ active, onClick, label, icon }: FilterPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs whitespace-nowrap border transition',
        active
          ? 'bg-foreground text-background border-foreground'
          : 'bg-muted text-muted-foreground border-border hover:text-foreground',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
